<template>
    <div class="page-full page-summary">
        <div class="content">
            <div class="content_inner">
                <div class="head">
                    <IconSvg class="icon" :name="'check'" />
                    <h1 class="title title--h1">
                        {{ $t('summary.title') }}
                    </h1>
                </div>
                <MTable :showLabels="false" :rows="tableData" :labels="tableLabel" />
                <div class="actions">
                    <Button @click="newOrder()" icon="ticket" size="big">
                        {{ $t('summary.new_order') }}
                    </Button>
                    <div class="secondary">
                        <Button @click="showOrder" icon="show" variant="secondary">
                            {{ $t('summary.show_order') }}
                        </Button>
                        <Button
                            v-if="canSendInvoice"
                            @click="vfm.open(sendInvoiceModalId)"
                            icon="invoice" variant="secondary">
                            Envoyer la facture
                        </Button>
                        <!-- Actions reçu CB Revolut (visible uniquement si la
                             commande a été payée via TPE Revolut) -->
                        <Button
                            v-if="revolutTransactionId && receiptUrl"
                            @click="vfm.open(receiptQrModalId)"
                            icon="invoice" variant="secondary">
                            Reçu CB
                        </Button>
                        <Button
                            v-if="revolutTransactionId"
                            @click="vfm.open(sendReceiptModalId)"
                            icon="send" variant="secondary">
                            Envoyer le reçu CB
                        </Button>
                        <Button
                            v-if="printStore.canPrint() && revolutTransactionId"
                            @click="printReceipt"
                            icon="printer" variant="secondary">
                            Imprimer le reçu CB
                        </Button>
                        <Button
                            v-if="printStore.canPrint()"
                            @click="printStore.printOrder(cartStore.order, orderStore.tickets)"
                            icon="printer" variant="secondary">
                            {{ $t('summary.print_order') }}
                        </Button>
                    </div>
                </div>
            </div>
        </div>

        <ModalConfirm :modal-id="sendInvoiceModalId" title="Envoyer une facture" @confirm="() => confirmSendInvoice()">
            <FormKit
                type="form"
                id="sendInvoiceModalForm"
                v-model="sendInvoiceData"
                :actions="false"
                @submit="handleInvoiceSubmit"
            >
                <FormKit
                    label="Email"
                    type="email"
                    name="email"
                    placeholder="Adresse e-mail"
                    :value="cartStore.order?.invoiceData?.email"
                    validation="required|email"
                />
                <div class="row--2cols">
                    <FormKit
                        type="text"
                        name="firstName"
                        label="Prénom"
                        :value="cartStore.order?.invoiceData?.firstName"
                        validation=""
                    />
                    <FormKit
                        type="text"
                        name="lastName"
                        label="Nom"
                        :value="cartStore.order?.invoiceData?.lastName"
                        validation=""
                    />
                </div>
                <div class="row--2cols">
                    <FormKit
                        type="text"
                        name="company"
                        :value="cartStore.order?.invoiceData?.company"
                        label="Société"
                        validation=""
                    />
                    <FormKit
                        type="text"
                        name="numTva"
                        label="Numéro de TVA"
                        :value="cartStore.order?.invoiceData?.numTva"
                        validation=""
                    />
                </div>
<FormKit
                    type="textarea"
                    name="address"
                    label="Adresse"
                    :value="cartStore.order?.invoiceData?.address"
                    validation=""
                />
            </FormKit>
        </ModalConfirm>

        <ModalConfirm :modal-id="sendReceiptModalId" title="Envoyer le reçu par email" @confirm="() => confirmSendReceipt()">
            <FormKit
                type="form"
                id="sendReceiptModalForm"
                v-model="sendReceiptData"
                :actions="false"
                @submit="handleReceiptSubmit"
            >
                <FormKit
                    label="Email du client"
                    type="email"
                    name="email"
                    placeholder="Adresse e-mail"
                    :value="cartStore.order?.invoiceData?.email"
                    validation="required|email"
                />
            </FormKit>
        </ModalConfirm>

        <RevolutReceiptQrModal
            v-if="receiptUrl"
            :modal-id="receiptQrModalId"
            :receipt-url="receiptUrl"
        />
    </div>
</template>
<script setup lang="ts">
    import IconSvg from '@/components/atoms/ASvg.vue'
    import MTable from '@/components/molecules/Table.vue'
    import Button from '@/components/atoms/Button.vue'
    import ModalConfirm from '@/components/molecules/modal/ModalConfirm.vue'
    import RevolutReceiptQrModal from '@/components/molecules/modal/RevolutReceiptQrModal.vue'
    import { ref, onMounted, computed } from 'vue';
    import { useVfm } from 'vue-final-modal'
    import { useCartStore } from '@/stores/CartStore';
    import { AppPrice } from '@/tools/Price';
    import {
        OrderStatusTypeEnum,
        PaymentMethodEnum,
        PaymentMethodTypeEnum,
    } from '@/interfaces/Order';
    import { useRouter } from 'vue-router';
    import { useEventStore } from '@/stores/EventStore';
    import { useAppStore } from '@/stores/AppStore';
    import { useOrderStore } from '@/stores/OrderStore';
    import { usePrintStore } from '@/stores/PrintStore';
    import { submitForm } from '@formkit/vue'
    import { paymentTerminalService } from '@/services/payment-terminal.service'
    import { toast } from 'vue3-toastify'
    import * as Sentry from '@sentry/browser'

    const printStore = usePrintStore()
    const eventStore = useEventStore()
    const router = useRouter()
    const appStore = useAppStore()
    const cartStore = useCartStore()
    const orderStore = useOrderStore()
    const vfm = useVfm()
    const sendInvoiceModalId = Symbol('sendInvoiceModalIdSummary')
    const sendInvoiceData = ref<any>(null)
    const sendReceiptModalId = Symbol('sendReceiptModalIdSummary')
    const sendReceiptData = ref<any>(null)
    const receiptQrModalId = Symbol('receiptQrModalIdSummary')

    // PaymentTransaction.id Revolut. Sert a generer / envoyer le recu CB.
    // Renseigne uniquement si la commande a ete payee via TPE Revolut.
    const revolutTransactionId = ref<string | null>(null)

    // URL publique du PDF du recu CB (= action du QR code).
    const receiptUrl = computed(() => {
        if (!revolutTransactionId.value || !cartStore.order?.token) return ''
        return paymentTerminalService.getReceiptPdfUrl(
            revolutTransactionId.value,
            cartStore.order.token,
        )
    })

    const fetchRevolutTransaction = async () => {
        if (!cartStore.order?._id) return
        try {
            const tx = await paymentTerminalService.findByOrderId(
                cartStore.order._id,
            )
            revolutTransactionId.value = tx?._id ?? null
        } catch (err) {
            Sentry.captureException(err)
            revolutTransactionId.value = null
        }
    }

    const printReceipt = async () => {
        if (!revolutTransactionId.value || !cartStore.order?.token) return
        try {
            const blob = await paymentTerminalService.getReceiptPdfBlob(
                revolutTransactionId.value,
                cartStore.order.token,
            )
            const url = URL.createObjectURL(blob)
            // Ouverture en nouvelle fenetre + impression auto au chargement.
            const win = window.open(url, '_blank')
            if (win) {
                win.addEventListener('load', () => win.print(), { once: true })
            }
        } catch (err) {
            Sentry.captureException(err)
            toast.error("Impossible de récupérer le reçu CB")
        }
    }

    const confirmSendReceipt = () => {
        submitForm('sendReceiptModalForm')
    }

    const handleReceiptSubmit = async () => {
        if (!revolutTransactionId.value || !sendReceiptData.value?.email) return
        try {
            await paymentTerminalService.sendReceiptByEmail(
                revolutTransactionId.value,
                sendReceiptData.value.email,
            )
            toast('Le reçu a bien été envoyé')
            vfm.close(sendReceiptModalId)
        } catch (err: any) {
            Sentry.captureException(err)
            toast.error("Impossible d'envoyer le reçu")
        }
    }

    const canSendInvoice = computed(() => {
        return (cartStore.order?.totalPrice?.sellingPrice ?? 0) > 0
    })

    const confirmSendInvoice = () => {
        submitForm('sendInvoiceModalForm')
    }

    const handleInvoiceSubmit = async () => {
        if (!cartStore.order) return
        await orderStore.sendInvoice(cartStore.order as any, sendInvoiceData.value)
        vfm.close(sendInvoiceModalId)
    }
    const orderData = ref({
        number: '',
        price: '',
        method: '',
        customMethod: ''
    })

    onMounted(async () => {
        appStore.isLoading = true

        // Modes asynchrones : on poll l'order jusqu'au statut completed
        // (webhook TPE / Stripe Terminal pas instantane). Sinon, fetch direct.
        const asyncMethods: string[] = [
            PaymentMethodTypeEnum.creditCardTerminal,
            PaymentMethodTypeEnum.creditCardTerminalRevolut,
        ]
        if (asyncMethods.includes(cartStore.paymentMethod.method)) {
            await waitForOrderCompleted(cartStore.order._id)
        } else {
            await cartStore.fetchOrder(cartStore.order._id)
        }
        await orderStore.fetchTicketsByOrder(cartStore.order)

        // Recupere le transactionId Revolut (si la commande a ete payee via TPE).
        // Non bloquant : si la commande n'est pas TPE Revolut, revolutTransactionId
        // reste null et les boutons "Recu CB" ne s'affichent pas.
        await fetchRevolutTransaction()

        // Total à afficher : on prend `totalWithAdjustments` quand il
        // est exposé par l'API (inclut l'assurance et autres
        // ajustements). Sinon fallback sur `sellingPrice` pour rester
        // compatible avec un payload ancien.
        const totalFinal =
            cartStore.order.totalPrice.totalWithAdjustments
            ?? cartStore.order.totalPrice.sellingPrice
        orderData.value = {
            number: cartStore.order.orderNumber,
            price: new AppPrice(totalFinal).divide100().toString(),
            method: "",
            customMethod: ""
        }
        appStore.isLoading = false

        // Impression automatique en fin de commande :
        // - canPrint() : un mode d'impression est selectionne et l'imprimante
        //   est connectee si requise
        // - shouldAutoPrint() : pour A4 QZ Tray, l'operateur a laisse le toggle
        //   "Imprimer automatiquement" actif (par defaut true). Pour les
        //   autres modes, retourne toujours true (comportement historique).
        if (printStore && printStore.canPrint() && printStore.shouldAutoPrint()) {
            await printStore.printOrder(cartStore.order, orderStore.tickets)
        }
    })

    const sleep = (ms: number) => {
        return new Promise((resolve) => {
            setTimeout(resolve, ms);
        });
    }

    // Polling de l'order jusqu'a status === 'completed' (ou timeout).
    // Utilise pour les paiements asynchrones (TPE Stripe / Revolut) ou
    // la bascule de l'order depend de la reception du webhook par l'API.
    const POLLING_INTERVAL_MS = 1000
    const POLLING_TIMEOUT_MS = 15000
    const waitForOrderCompleted = async (orderId: string): Promise<void> => {
        const start = Date.now()
        while (Date.now() - start < POLLING_TIMEOUT_MS) {
            await cartStore.fetchOrder(orderId)
            if (cartStore.order?.status === OrderStatusTypeEnum.COMPLETED) {
                return
            }
            await sleep(POLLING_INTERVAL_MS)
        }
        // Timeout : on continue avec ce qu'on a (status pas encore completed
        // cote DB malgre le paiement reussi). Le BO/SummaryView affichera
        // ce qui est dispo, le webhook finira de remettre les choses au propre.
    }

    const showOrder = (() => {
        router.push({name: 'order_details', params: { orderId: cartStore.order._id }})
    })

    const newOrder = async () => {
        const eventId = eventStore.currentEvent._id
        if (cartStore.order.isProduct) {
            router.push({name: 'buvette'})
        }
        else {
            router.push({name: 'ticket', params: { eventId }})
        }
    }

    const paymentMethodLabel = computed(() => {
        let methodLabel = "--"
        if (cartStore.order.payments) {
            methodLabel = cartStore.order.payments.map((payment: any) => {
                const price = new AppPrice(payment.amount).divide100().toString()
                if (payment.method == PaymentMethodTypeEnum.custom) {
                    return `${payment.customMethod.label} (${price})`
                }
                return `${PaymentMethodEnum[payment.method]} (${price})`

            }).join(', ')
        }
        return methodLabel
    })

    const tableLabel = computed(() => {
        return [
            {
                label: "Libellé",
                value: "label",
                align: "left"
            },
            {
                label: "Valeur",
                value: "value",
                align: "right"
            }
        ]
    })

    const itemLabel = (cartStore.order.isProduct) ? 'produits' : 'tickets'

    // Détection des ajustements présents sur la commande finalisée.
    // L'assurance est lue depuis order.hasInsurance + order.insurance (champs
    // historiques). Les frais d'édition sont lus depuis
    // order.totalPrice.adjustments en filtrant type === "editionFees".
    const hasInsurance = computed(() => {
        return cartStore.order?.hasInsurance && cartStore.order?.insurance?.amount > 0
    })

    const insurancePrice = computed(() => {
        const amount = cartStore.order?.insurance?.amount ?? 0
        return new AppPrice(amount).divide100().toString()
    })

    const editionFeesAdjustment = computed(() => {
        const adjustments = cartStore.order?.totalPrice?.adjustments
        if (!adjustments) return null
        return adjustments.find(a => a.type === 'editionFees') ?? null
    })

    const hasEditionFees = computed(() => {
        return editionFeesAdjustment.value !== null && editionFeesAdjustment.value.amount > 0
    })

    const editionFeesLabel = computed(() => {
        return editionFeesAdjustment.value?.label || "Frais d'edition"
    })

    const editionFeesPrice = computed(() => {
        const amount = editionFeesAdjustment.value?.amount ?? 0
        return new AppPrice(amount).divide100().toString()
    })

    // sellingPrice = sous-total HORS ajustements (tickets + addons).
    // totalWithAdjustments = montant final (inclut assurance + frais d'édition).
    const subTotal = computed(() => {
        const selling = cartStore.order?.totalPrice?.sellingPrice ?? 0
        return new AppPrice(selling).divide100().toString()
    })

    const tableData = computed(() => {
        const rows: { label: string; value: string | number }[] = [
            {
                label: "Numéro de commande",
                value: orderData.value.number
            },
        ]

        // Cas possibles :
        // - Pas d'ajustement : 1 ligne "Montant de la commande"
        // - 1 seul ajustement (assurance OU frais d'édition) : 3 lignes
        //   (sous-total libellé "Montant (hors X)" + ajustement + total)
        // - Les deux : 4 lignes (sous-total "Sous-total" + assurance +
        //   frais d'édition + total)
        const adjustmentCount = (hasInsurance.value ? 1 : 0) + (hasEditionFees.value ? 1 : 0)

        if (adjustmentCount === 0) {
            rows.push({
                label: "Montant de la commande",
                value: orderData.value.price
            })
        } else {
            // Libellé du sous-total selon les ajustements présents
            let subTotalLabel = "Sous-total"
            if (adjustmentCount === 1) {
                if (hasInsurance.value) subTotalLabel = "Montant (hors assurance)"
                else subTotalLabel = `Montant (hors ${editionFeesLabel.value.toLowerCase()})`
            }

            rows.push({
                label: subTotalLabel,
                value: subTotal.value
            })

            if (hasInsurance.value) {
                rows.push({
                    label: "Assurance",
                    value: insurancePrice.value
                })
            }

            if (hasEditionFees.value) {
                rows.push({
                    label: editionFeesLabel.value,
                    value: editionFeesPrice.value
                })
            }

            rows.push({
                label: "Montant total",
                value: orderData.value.price
            })
        }

        rows.push(
            {
                label: "Réglé par",
                value: paymentMethodLabel.value
            },
            {
                label: `Nombre de ${itemLabel}`,
                value: orderStore.tickets.length
            },
        )

        return rows
    })

</script>